// frontend/src/ReleaseNotes/releaseNotesPublishedSelectors.ts
import type {
  IImpact,
  IModule,
  IReleaseItem,
  IReleaseNote,
  IReleaseType,
} from "@multiconsult-group/multiplacer-core";
import { openapi } from "../../openapi"; // <- justera om nødvendig
import type { IRootState } from "../app/store";
import { createSelector } from "@reduxjs/toolkit";
import {
  selectReleaseNotesSelectedModules,
  selectReleaseNotesSelectedTypes,
  selectReleaseNotesSelectedImpacts,
} from "./releaseNotesPublicSlice";

/* -------------------------------------------------------------------------- */
/*  Cache selector                                                            */
/* -------------------------------------------------------------------------- */

const EMPTY_PUBLISHED_RELEASE_NOTES: IReleaseNote[] = [];

export const selectHttpMultiplacerReleaseNotesPublished = (state: IRootState): IReleaseNote[] => {
  // Endpoint har .input(z.void()) -> args er undefined
  return (
    openapi.endpoints.getMultiplacerReleaseNotesPublished.select(undefined)(state).data ??
    EMPTY_PUBLISHED_RELEASE_NOTES
  );
};

/* -------------------------------------------------------------------------- */
/*  Building blocks                                                           */
/* -------------------------------------------------------------------------- */

export const selectHttpMultiplacerReleaseNotesPublishedItems = (
  state: IRootState
): IReleaseNote[] => {
  return selectHttpMultiplacerReleaseNotesPublished(state);
};

const matchesFilter = (args: {
  item: IReleaseItem;
  selectedModules: readonly IModule[];
  selectedTypes: readonly IReleaseType[];
  selectedImpacts: readonly IImpact[];
}): boolean => {
  const { item, selectedModules, selectedTypes, selectedImpacts } = args;

  const moduleOk = selectedModules.length === 0 || selectedModules.includes(item.module);
  const typeOk = selectedTypes.length === 0 || selectedTypes.includes(item.type);
  const impactOk =
    selectedImpacts.length === 0 || (item.impact != null && selectedImpacts.includes(item.impact));

  return moduleOk && typeOk && impactOk;
};

export const selectReleaseNotesPublishedFiltered = createSelector(
  [
    (state: IRootState) => selectHttpMultiplacerReleaseNotesPublishedItems(state),
    (state: IRootState) => selectReleaseNotesSelectedModules(state),
    (state: IRootState) => selectReleaseNotesSelectedTypes(state),
    (state: IRootState) => selectReleaseNotesSelectedImpacts(state),
  ],
  (releases, selectedModules, selectedTypes, selectedImpacts): IReleaseNote[] => {
    const hasFilters =
      selectedModules.length > 0 || selectedTypes.length > 0 || selectedImpacts.length > 0;

    if (!hasFilters) {
      return releases;
    }

    const out: IReleaseNote[] = [];

    for (const release of releases) {
      const filteredItems = (release.items ?? []).filter((item) =>
        matchesFilter({
          item,
          selectedModules,
          selectedTypes,
          selectedImpacts,
        })
      );

      if (filteredItems.length === 0) continue;

      out.push({
        ...release,
        items: filteredItems,
      });
    }

    return out;
  }
);

/* -------------------------------------------------------------------------- */
/*  Total count (sum of visible items)                                        */
/* -------------------------------------------------------------------------- */

export const selectReleaseNotesPublishedFilteredTotalCount = createSelector(
  [(state: IRootState) => selectReleaseNotesPublishedFiltered(state)],
  (filteredReleases): number => {
    let count = 0;
    for (const r of filteredReleases) {
      count += (r.items ?? []).length;
    }
    return count;
  }
);
